/** Workflows P5-3: path actions, the paths jump bar, undo / redo, problems shown on tiles and steps, and notes. */
import { workflowsService } from "./workflows-service";
import { workflowsTriggers } from "./workflows-triggers";

export const workflowsExtras = {
  history: {
    toolbar: "Edit history",
    undo: "Undo",
    redo: "Redo",
    undoAction: "Undo: {{action}}",
    redoAction: "Redo: {{action}}",
    nothingToUndo: "Nothing to undo",
    nothingToRedo: "Nothing to redo",
    undone: "Undid: {{action}}.",
    redone: "Redid: {{action}}.",
    addStep: "add {{step}} to {{path}}",
    removeStep: "remove {{step}} from {{path}}",
    moveStep: "move {{step}}",
    replaceStep: "replace {{from}} with {{to}}",
    editStep: "edit {{step}}",
    connect: "connect {{exit}} to {{path}}",
    disconnect: "disconnect {{exit}}",
    addPath: "add {{path}}",
    deletePath: "delete {{path}}",
    renamePath: "rename {{path}}",
    duplicatePath: "duplicate {{path}}",
    addNote: "add a note",
    editNote: "edit a note",
    deleteNote: "delete a note",
    settings: "change the settings",
    translations: "edit the translations",
    generic: "edit the workflow",
  },
  paths: {
    actions: "Actions for {{path}}",
    rename: "Rename",
    duplicate: "Duplicate",
    delete: "Delete",
    nameLabel: "Name of {{path}}",
    namePlaceholder: "Name this path",
    copyName: "{{name}} (copy)",
    duplicated: "Duplicated as {{path}}.",
    renamed: "Renamed to {{path}}.",
    full: "All {{max}} paths are used. Delete one before duplicating.",
    deleteTitle: "Delete {{path}}?",
    deleteBody_one: "{{count}} connection leads here and will be cleared. Steps in this path are removed.",
    deleteBody_other: "{{count}} connections lead here and will be cleared. Steps in this path are removed.",
    deleteBodyNone: "No connection leads here. Steps in this path are removed.",
    deleteNotes_one: "Its {{count}} note stays as a note of the workflow.",
    deleteNotes_other: "Its {{count}} notes stay as notes of the workflow.",
    deleteConfirm: "Delete path",
    deleted: "Deleted {{path}}.",
  },
  jumpBar: {
    label: "Paths",
    list: "List of paths",
    goTo: "Go to {{path}}",
    hasProblems: "has problems",
    current: "current path",
    noPaths: "No paths",
  },
  problemCount_one: "{{count}} problem",
  problemCount_other: "{{count}} problems",
  publishHint_one: "Fix {{count}} problem to publish",
  publishHint_other: "Fix {{count}} problems to publish",
  problemsHere: "Problems in {{path}}",
  notesChanged: "Notes changed",
  limit: {
    minutes_one: "{{count}} minute",
    minutes_other: "{{count}} minutes",
    hours_one: "{{count}} hour",
    hours_other: "{{count}} hours",
    days_one: "{{count}} day",
    days_other: "{{count}} days",
  },
  problemParts: { text: "text", field_label: "field label", buttons: "buttons", label: "button label" },
  problems: {
    ...workflowsTriggers.problems,
    ...workflowsService.problems,
    no_summary_here: "This trigger starts before a conversation exists, so there is nothing to summarise.",
    no_first_path: "The workflow must start with Path A.",
    unknown_path: "Goes to Path {{path}}, which does not exist.",
    duplicate_step: "Step id {{step}} is used twice.",
    exit_unset: "Choose a path for this exit.",
    loop: "Path {{path}} leads back to itself. Only a path that waits for the visitor can loop.",
    invalid_field: "“{{field}}” is missing or not valid.",
    note_unknown_path: "A note is on Path {{path}}, which does not exist.",
    not_facing: "This trigger has no visitor to talk to.",
    needs_facing: "This trigger needs a step the visitor sees or answers.",
    needs_intent: "Choose which intent starts this workflow.",
    needs_conversation: "This trigger has no conversation for this step.",
    missing_target_hand: "Choose who to hand the conversation to.",
    missing_target_assign: "Choose who to assign to.",
    missing_target_notify: "Choose who to notify.",
    bad_template: "The custom body is not valid JSON. Put every variable inside quotes, as in {{example}}.",
    no_wait_here: "This trigger starts before a conversation exists, so it cannot wait.",
    no_rating_here: "This trigger has no conversation to rate.",
    no_interrupt_here: "This trigger has no conversation to interrupt on.",
    needs_minutes: "Choose after how many minutes this workflow starts.",
    needs_value: "Choose a value to compare {{field}} with.",
    needs_number: "{{field}} is compared with a number.",
    reusable_settings: "A reusable workflow has no settings; it runs when it is called.",
    no_hook_here: "This trigger starts before a conversation exists, so it cannot wait for a webhook.",
    no_reply_timer_here: "This trigger starts before a conversation exists, so it cannot time out a reply.",
    no_snooze_here: "This trigger starts before a conversation exists, so there is nothing to snooze.",
    start_over_in_a: "Start over goes back to Path A, so it cannot be used inside Path A.",
    translation_unknown_step: "A translation names a step that does not exist.",
    translation_not_translatable: "This step has no {{what}} to translate.",
    translation_unknown_button: "A translation names button {{button}}, which does not exist.",
    needs_plan: "Needs the {{plan}} plan or higher.",
    wait_too_long: "Waits up to {{limit}} on your plan. Shorten it or upgrade.",
    connector_forbidden_header: "The header {{header}} can't be set in a workflow. Put credentials on the endpoint in Settings → Webhooks.",
    connector_duplicate_header: "The header {{header}} is set twice.",
    connector_body_on_get: "A GET request can't have a body.",
    connector_bad_body: "The request body is not valid JSON. Put every variable inside quotes, as in {{example}}.",
    connector_map_pointer: "A field of the answer must be a path that starts with a slash, like /data/status.",
    connector_map_variable: "A saved variable uses letters, numbers and _ only.",
    connector_duplicate_output: "The output {{name}} is copied twice.",
    connector_duplicate_target: "{{variable}} is filled by more than one field.",
    connector_bad_path: "The path can't contain a . or .. part, not even an encoded one.",
    connector_path_format: "The path must start with a slash and can't contain ?, #, @, a backslash or a space. Use the query parameters for ? values.",
    connector_row: "A query parameter or a header has an invalid name or a value that is too long.",
    connector_timeout_facing: "The visitor waits for this request, so it can take at most {{seconds}} seconds.",
  },
  notes: {
    title: "Notes",
    strip: "Notes about this workflow",
    add: "Add note",
    addToPath: "Add note to {{path}}",
    empty: "No notes yet. Leave a note for whoever edits this workflow next.",
    emptyHint: "Notes are for your team. Visitors never see them.",
    label: "Note",
    placeholder: "Write a note for your team",
    save: "Save note",
    delete: "Delete note",
    deleted: "Note deleted.",
    count_one: "{{count}} note",
    count_other: "{{count}} notes",
    full: "A workflow can have at most {{max}} notes.",
    forPath: "On {{path}}",
    forWorkflow: "On the workflow",
    open: "Notes of {{path}}",
    openWorkflow: "Notes of the workflow",
    chars: "{{count}} of {{max}}",
  },
} as const;
